import { useState, type CSSProperties, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { kstToday, overtimeMinutes } from '@dxtech/shared';
import { overtimeApi } from '../api/overtime.js';
import { Spinner } from '../ui/common.js';
import { OvertimeBadge, actualSpan, dur, planSpan } from './Overtime.js';

/** 통계 분석 — 개인 야근 이력(월별). 대상 선택은 통계 화면의 직원 목록을 그대로 씀 */
export function OvertimeHistory({ people, defaultUpn }: { people: Array<{ upn: string; name: string }>; defaultUpn: string }): JSX.Element {
  const [upn, setUpn] = useState('');
  const [month, setMonth] = useState(kstToday().slice(0, 7));
  const target = upn || defaultUpn;
  const q = useQuery({ queryKey: ['overtime', 'history', target, month], queryFn: () => overtimeApi.history(target, month), enabled: !!target });
  const items = [...(q.data?.items ?? [])].sort((a, b) => b.date.localeCompare(a.date));
  const total = items.reduce((m, r) => m + overtimeMinutes(r), 0);
  const md = (date: string) => { const d = new Date(`${date}T00:00:00Z`); return `${d.getUTCMonth() + 1}/${d.getUTCDate()}(${'일월화수목금토'[d.getUTCDay()]})`; };
  const cell: CSSProperties = { padding: '10px 14px', fontSize: 13, borderTop: '1px solid #F3F4F6', whiteSpace: 'nowrap' };

  return (
    <div className="dx-card" style={{ overflow: 'hidden', marginTop: 16 }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>개인 야근 이력</div>
        <span style={{ fontSize: 12.5, color: '#6B7280' }}>{items.length}건, 실제 {dur(total)}</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
          <select className="dx-select" aria-label="직원" value={target} onChange={(e) => setUpn(e.target.value)}>
            {people.map((p) => <option key={p.upn} value={p.upn}>{p.name}</option>)}
          </select>
          <input type="month" className="dx-input" aria-label="조회 월" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        </span>
      </div>
      {q.isLoading ? <div style={{ padding: 28, textAlign: 'center' }}><Spinner size={20} /></div> : items.length === 0 ? (
        <div style={{ padding: 28, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>{q.error ? (q.error as Error).message : '해당 월 야근 이력이 없습니다.'}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr style={{ background: '#F9FAFB', textAlign: 'left', fontSize: 12, color: '#6B7280' }}>
              {['날짜', '예정', '실제', '시간', '상태', '업무 내용'].map((h) => <th key={h} style={{ padding: '9px 14px', fontWeight: 600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...cell, fontWeight: 600 }}>{md(r.date)}</td>
                  <td style={cell} className="tnum">{planSpan(r)}</td>
                  <td style={cell} className="tnum">{actualSpan(r) || '-'}</td>
                  <td style={cell}>{r.actualStart ? dur(overtimeMinutes(r)) : '-'}</td>
                  <td style={cell}><OvertimeBadge status={r.status} /></td>
                  <td style={{ ...cell, whiteSpace: 'normal', color: '#4B5563' }}>{r.reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
